/**
 * The 50 status emblems (backend engine/defaults.ts STATUSES), drawn as SVG.
 *
 * One style for all fifty: the BAND sets the frame (its shape and material:
 * cracked black stone, dark iron, carved oak, grey granite, bronze, silver,
 * gold, platinum, diamond facets, and Mythic living light that slowly turns
 * and glows), the STATUS sets the figure in the middle (a skull for Dead, a
 * lantern for Traveler, a crown for Duke, the sun for Legendary).
 *
 * Two levels of detail: at 32 px and below (the chip beside a name) the frame
 * is plain and the figure larger, so it reads at 20 px; above, the frame gets
 * its texture (cracks, rivets, grain, speckles, beads, facets, rays).
 *
 *   statusEmblemSvg(n, { size, animate })   the SVG text
 *   statusEmblemUri(n, { size, animate })   a data: URI for <img> (memoised)
 *
 * Used by components/StatusEmblem.tsx and scripts/make-status-emblems.mjs
 * (static files in public/status/<slug>.svg). No imports, and only erasable
 * TypeScript, so Node can run it directly for the script.
 */

export const STATUS_BANDS = [
  { band: 'Fallen', frame: 'cracked black stone' },
  { band: 'Shadowed', frame: 'dark iron' },
  { band: 'Wood', frame: 'carved oak' },
  { band: 'Stone', frame: 'grey granite' },
  { band: 'Bronze', frame: 'bronze' },
  { band: 'Silver', frame: 'silver' },
  { band: 'Gold', frame: 'gold' },
  { band: 'Platinum', frame: 'platinum' },
  { band: 'Diamond', frame: 'diamond' },
  { band: 'Mythic', frame: 'living light' },
];

export const STATUS_NAMES = [
  'Dead', 'Banished', 'Prisoner', 'Outlaw', 'Scammer',
  'Fraud', 'Troublemaker', 'Suspect', 'Doubted', 'Stranger',
  'Newcomer', 'Visitor', 'Wanderer', 'Traveler', 'Settler',
  'Neighbor', 'Regular', 'Member', 'Companion', 'Trusted',
  'Respected', 'Craftsman', 'Merchant', 'Trader', 'Notable',
  'Mentor', 'Veteran', 'Leader', 'Captain', 'Commander',
  'Elder', 'Patron', 'Noble', 'Baron', 'Count',
  'Duke', 'Prince', 'Champion', 'Hero', 'Star',
  'Celebrity', 'Tycoon', 'Magnate', 'Mogul', 'Icon',
  'Luminary', 'Myth', 'Titan', 'Immortal', 'Legendary',
];

const clampN = (n: number): number => Math.max(1, Math.min(50, Math.round(n) || 10));
export const statusName = (n: number): string => STATUS_NAMES[clampN(n) - 1];
export const statusSlug = (n: number): string => statusName(n).toLowerCase();
export const bandIndex = (n: number): number => Math.floor((clampN(n) - 1) / 5);
export const bandOf = (n: number): { band: string; frame: string } => STATUS_BANDS[bandIndex(n)];

/** Colours per band: frame material (light → dark), frame edge, field (centre → rim), figure, inner ring. */
interface Pal { m: string[]; sheen: boolean; edge: string; field: [string, string]; fig: string; ring: string; text: string }
const PAL: Pal[] = [
  { m: ['#55555c', '#2a2a2f', '#0b0b0d'], sheen: false, edge: '#000000', field: ['#3a1a1a', '#0e0707'], fig: '#ece4d2', ring: '#9b2a22', text: '#b4413a' },
  { m: ['#959ba4', '#565b63', '#24272c'], sheen: true, edge: '#121417', field: ['#323740', '#121418'], fig: '#cfd5dc', ring: '#6b717a', text: '#5d636c' },
  { m: ['#d29a5f', '#a26834', '#5c3717'], sheen: false, edge: '#3a210d', field: ['#f6e6c4', '#dcbc8b'], fig: '#5a3213', ring: '#7d4b20', text: '#8a5523' },
  { m: ['#dcdee1', '#a6aaaf', '#6b7076'], sheen: false, edge: '#454950', field: ['#555b62', '#2c3035'], fig: '#f3f4f5', ring: '#959aa0', text: '#6b7076' },
  { m: ['#f8c690', '#c57b3b', '#743f15'], sheen: true, edge: '#46240a', field: ['#633a18', '#2e190a'], fig: '#f8c893', ring: '#e8a364', text: '#a8622a' },
  { m: ['#ffffff', '#c3cbd4', '#7f8b98'], sheen: true, edge: '#55606b', field: ['#3a4756', '#18202a'], fig: '#f6f8fb', ring: '#cfd6de', text: '#6f7c8a' },
  { m: ['#fff4b8', '#f1c23b', '#a8710a'], sheen: true, edge: '#664404', field: ['#74490a', '#382303'], fig: '#ffe38e', ring: '#ffd34f', text: '#b07c0a' },
  { m: ['#ffffff', '#d5dee9', '#93a3b8'], sheen: true, edge: '#5a6a80', field: ['#283a55', '#0e1828'], fig: '#e9f2fc', ring: '#c2d1e3', text: '#5f7590' },
  { m: ['#f4feff', '#9fe2ff', '#2f97d0'], sheen: true, edge: '#175a80', field: ['#0d3a5c', '#041523'], fig: '#d3f7ff', ring: '#86e0ff', text: '#2386c0' },
  { m: ['#fff8d6', '#ffb340', '#a646f2'], sheen: true, edge: '#40127a', field: ['#45127a', '#100326'], fig: '#fff5d6', ring: '#ffd76e', text: '#9b3fe0' },
];

/** The band's text colour, for a name beside the emblem (readable on white and on dark grey). */
export const bandText = (n: number): string => PAL[bandIndex(n)].text;

// ── Little helpers ───────────────────────────────────────────────────────────

const f1 = (v: number) => +v.toFixed(2);
const poly = (pts: [number, number][]) => 'M' + pts.map(([x, y]) => `${f1(x)} ${f1(y)}`).join('L') + 'Z';
const ring = (cx: number, cy: number, n: number, r1: number, r2: number, rot = -90): [number, number][] =>
  Array.from({ length: n * 2 }, (_, i) => {
    const a = ((rot + (i * 180) / n) * Math.PI) / 180;
    const r = i % 2 ? r2 : r1;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  });
const around = (cx: number, cy: number, n: number, r: number, rot = -90): [number, number][] =>
  Array.from({ length: n }, (_, i) => {
    const a = ((rot + (i * 360) / n) * Math.PI) / 180;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  });
/** A deterministic pseudo-random sequence, so every render is identical. */
function rnd(seed: number) { let s = seed; return () => ((s = (s * 16807) % 2147483647) / 2147483647); }

// ── The 50 figures, in a 100-unit box, centred on 50,50 ──────────────────────

type Glyph = (F: string, B: string) => string;
const P = (d: string, F: string, extra = '') => `<path d="${d}" fill="${F}"${extra}/>`;
const S = (d: string, C: string, w: number, extra = '') => `<path d="${d}" fill="none" stroke="${C}" stroke-width="${w}" stroke-linecap="round" stroke-linejoin="round"${extra}/>`;
const C = (cx: number, cy: number, r: number, F: string, extra = '') => `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${F}"${extra}/>`;
const R = (x: number, y: number, w: number, h: number, F: string, rx = 0, extra = '') => `<rect x="${x}" y="${y}" width="${w}" height="${h}"${rx ? ` rx="${rx}"` : ''} fill="${F}"${extra}/>`;

function laurel(F: string): string {
  let out = S('M47 71C34 66 29 52 33 37M53 71C66 66 71 52 67 37', F, 2.6);
  for (const t of [115, 140, 165, 190, 215]) {
    const a = (t * Math.PI) / 180;
    const x = 50 + 19 * Math.cos(a), y = 53 + 19 * Math.sin(a), rot = t + 65;
    out += `<ellipse cx="${f1(x)}" cy="${f1(y)}" rx="6.4" ry="2.9" transform="rotate(${f1(rot)} ${f1(x)} ${f1(y)})" fill="${F}"/>`;
    out += `<ellipse cx="${f1(100 - x)}" cy="${f1(y)}" rx="6.4" ry="2.9" transform="rotate(${f1(180 - rot)} ${f1(100 - x)} ${f1(y)})" fill="${F}"/>`;
  }
  return out;
}

function sword(F: string, rot: number): string {
  return `<g transform="rotate(${rot} 50 50)">` + P('M47.9 60V29l2.1-5 2.1 5v31z', F) + R(41.5, 59.4, 17, 3.4, F, 1.7) + R(48.3, 62.6, 3.4, 7, F) + C(50, 71.6, 2.6, F) + '</g>';
}

const GLYPHS: Glyph[] = [
  // 1 Dead — a skull
  (F, B) => P('M50 29c-11.6 0-20 8.2-20 19.2 0 6.2 2.9 10.8 7.4 13.6V68c0 1.7 1.3 3 3 3h19.2c1.7 0 3-1.3 3-3v-6.2c4.5-2.8 7.4-7.4 7.4-13.6C70 37.2 61.6 29 50 29z', F)
    + `<ellipse cx="42.2" cy="48.5" rx="5.2" ry="5.8" fill="${B}"/><ellipse cx="57.8" cy="48.5" rx="5.2" ry="5.8" fill="${B}"/>`
    + P('M50 55.5l-3.2 5.4h6.4z', B) + S('M45 64.6v6.4M50 64.6v6.4M55 64.6v6.4', B, 1.8),
  // 2 Banished — leaving through a broken ring
  (F) => S('M58.6 34.8A17.4 17.4 0 1 0 58.6 65.2', F, 6.5) + S('M43 50h25.5', F, 6.5) + S('M60.5 41.2L69.5 50l-9 8.8', F, 6.5),
  // 3 Prisoner — a chain, one link broken
  (F) => '<g transform="rotate(-32 50 50)">' + `<rect x="25.5" y="43" width="27" height="14" rx="7" fill="none" stroke="${F}" stroke-width="5.2"/>`
    + S('M66.5 43H55a7 7 0 0 0 0 14H66.5', F, 5.2) + S('M70.2 44.6l3.6-4.2M70.2 55.4l3.6 4.2', F, 4.4) + '</g>',
  // 4 Outlaw — a bandit's mask
  (F, B) => P('M26.5 45.5c4.6-4.8 13.8-6.4 23.5-2.6 9.7-3.8 18.9-2.2 23.5 2.6-.4 9.2-5.7 14.8-12.6 14.8-5.1 0-8.6-3-10.9-6.8-2.3 3.8-5.8 6.8-10.9 6.8-6.9 0-12.2-5.6-12.6-14.8z', F)
    + P('M35 50.2c2.1-2.6 7-3 10-.6-1.5 3.2-4.5 4.6-7 4-1.7-.3-2.7-1.7-3-3.4z', B) + P('M65 50.2c-2.1-2.6-7-3-10-.6 1.5 3.2 4.5 4.6 7 4 1.7-.3 2.7-1.7 3-3.4z', B)
    + S('M27 46.5l-6.5-4.5M27 49l-7 1.5', F, 2.8),
  // 5 Scammer — a fish hook
  (F) => S('M57 34v22.5a9.5 9.5 0 0 1-19 0V51', F, 6) + P('M38 42.2l-5.4 9.6h10.8z', F) + `<circle cx="57" cy="29.8" r="4.3" fill="none" stroke="${F}" stroke-width="3.2"/>`,
  // 6 Fraud — a forged coin, split in two
  (F, B) => {
    const jag = 'M50 31L45.5 38.5 52.5 45 45.5 52 52.5 58.5 48 69';
    return `<g transform="translate(-2.6 1.6)">${P(jag + 'A19 19 0 0 1 50 31z', F)}</g><g transform="translate(2.6 -1.6)">${P(jag + 'A19 19 0 1 0 50 31z', F)}</g>`
      + S('M38 44.5a13 13 0 0 1 6-8.5M62 55.5a13 13 0 0 1-6 8.5', B, 2);
  },
  // 7 Troublemaker — a lit bomb
  (F, B) => C(46, 56, 15.5, F) + `<rect x="50.5" y="35.5" width="10.5" height="8.5" rx="1.6" transform="rotate(40 55.7 39.7)" fill="${F}"/>`
    + S('M58.5 35.5c2-4 5.2-5.8 9-4.8', F, 2.8) + P('M69.5 23.5l1.8 5 5 1.8-5 1.8-1.8 5-1.8-5-5-1.8 5-1.8z', F) + S('M37.5 51a9.5 9.5 0 0 1 6.5-6.5', B, 2.8),
  // 8 Suspect — a magnifying glass
  (F, B) => `<circle cx="45" cy="45" r="12.5" fill="none" stroke="${F}" stroke-width="6.2"/>` + S('M54.5 54.5L67.5 67.5', F, 8.5) + S('M39 42a7 7 0 0 1 4.5-4.5', F, 2.4, ` opacity="0.7"`) + (B ? '' : ''),
  // 9 Doubted — a question mark
  (F) => S('M40.5 40.5a9.5 9.5 0 1 1 13.8 8.5c-3 1.5-4.3 3.4-4.3 6.5v2', F, 6.8) + C(50, 68, 4.2, F),
  // 10 Stranger — a hooded figure
  (F, B) => P('M50 27c-11 0-18.5 9-18.5 20.5v7c-4.8 3-7.5 7.8-7.5 14.5h52c0-6.7-2.7-11.5-7.5-14.5v-7C68.5 36 61 27 50 27z', F)
    + P('M50 37c-6 0-9.6 5-9.6 11.6 0 5.6 3.6 10.4 9.6 10.4s9.6-4.8 9.6-10.4C59.6 42 56 37 50 37z', B) + C(46.4, 49, 1.5, F) + C(53.6, 49, 1.5, F),
  // 11 Newcomer — a sprout
  (F) => S('M50 70V50', F, 4.6) + P('M50 53c-2-9.5-9.5-14.8-20-13.8 0 10.6 7.5 16.4 20 13.8z', F) + P('M50 48c1-10.5 8.5-17 20-17 1 11.6-6.4 18-20 17z', F) + S('M35 71h30', F, 4.2),
  // 12 Visitor — a tent
  (F, B) => P('M50 30L27 69.5h46z', F) + P('M50 48.5l-8 21h16z', B) + S('M50 30.5l-4-5.5M50 30.5l4-5.5', F, 2.8),
  // 13 Wanderer — a compass
  (F, B) => `<circle cx="50" cy="50" r="19" fill="none" stroke="${F}" stroke-width="3.6"/>` + P(poly(ring(50, 50, 4, 15.5, 4.2)), F) + P(poly(ring(50, 50, 4, 9, 2.4, -45)), F)
    + C(50, 50, 2.4, B) + P('M50 25.5l-3 4.5h6z', F),
  // 14 Traveler — a lantern
  (F, B) => S('M45 33.5a5 5 0 0 1 10 0', F, 2.8) + P('M40.5 39h19l-3.2-5.8H43.7z', F) + R(40, 38.6, 20, 26, F, 2) + R(44, 43, 12, 17.5, B, 1.5)
    + P('M50 45c-2.7 3.5-3.8 5.8-3.8 7.9a3.8 3.8 0 0 0 7.6 0c0-2.1-1.1-4.4-3.8-7.9z', F) + P('M37.5 64h25v3.6a2 2 0 0 1-2 2h-21a2 2 0 0 1-2-2z', F),
  // 15 Settler — a house
  (F, B) => P('M50 29.5L26 50.5h6v19.5h36V50.5h6z', F) + P('M60.5 31h5.5v11.5l-5.5-4.8z', F) + R(45.3, 56, 9.4, 14, B, 1) + R(35.5, 53, 6.4, 6.4, B) + R(58.1, 53, 6.4, 6.4, B),
  // 16 Neighbor — a raised open hand
  (F) => R(36.6, 37, 5.4, 22, F, 2.7) + R(42.8, 31.5, 5.4, 26, F, 2.7) + R(49, 30, 5.4, 27, F, 2.7) + R(55.2, 33, 5.4, 25, F, 2.7)
    + P('M36.6 50h24v9.5c0 7.6-5 12-12 12s-12-4.4-12-12z', F) + P('M59.8 57.5l6.4-9.2c1.6-2.3 4.6-1.6 5.1.7.3 1.4-.1 2.6-.9 3.8l-7.6 11z', F),
  // 17 Regular — a tankard of ale
  (F, B) => R(32, 41, 26, 29, F, 3) + `<path d="M58 46.5h4.5a5.5 5.5 0 0 1 5.5 5.5v4a5.5 5.5 0 0 1-5.5 5.5H58" fill="none" stroke="${F}" stroke-width="4.6"/>`
    + P('M30.5 43c0-4.2 3.1-6.8 6.3-6.5 1-3.6 4.8-5.5 8.4-4.2 2.4-2.7 6.8-2.9 9.4-.2 3.4-.4 6.3 2.1 6.3 5.6 0 3.2-1.6 5.3-4.2 5.3z', F)
    + S('M32.5 43.4h25', B, 1.6) + S('M39 49v15M45 49v15M51 49v15', B, 2.2),
  // 18 Member — a key
  (F) => `<circle cx="36.5" cy="50" r="9.8" fill="none" stroke="${F}" stroke-width="5.8"/>` + R(45, 47, 27, 6, F, 1.5) + R(60, 51, 4.8, 8.5, F) + R(66.8, 51, 4.8, 6.5, F),
  // 19 Companion — two friends
  (F, B) => C(58.5, 38.5, 6.8, F) + P('M45 67c0-9.4 6-16 13.5-16S72 57.6 72 67z', F)
    + `<g stroke="${B}" stroke-width="2.6">` + C(42.5, 42.5, 7.8, F) + P('M27 71.5c0-10.8 6.9-18.5 15.5-18.5S58 60.7 58 71.5z', F) + '</g>',
  // 20 Trusted — a seal with a tick
  (F, B) => P('M50 27.5l18 6.5v13c0 12.4-7.6 20.4-18 25-10.4-4.6-18-12.6-18-25V34z', F) + S('M40.5 49.5l6.5 6.5 13-13.5', B, 5.2),
  // 21 Respected — a rosette
  (F, B) => P('M42 52l-6.5 19.5 6.2-3.4 3.6 5.6 4.3-17.5z', F) + P('M58 52l6.5 19.5-6.2-3.4-3.6 5.6-4.3-17.5z', F)
    + P(poly(ring(50, 44, 12, 15, 12.6)), F) + `<circle cx="50" cy="44" r="8.6" fill="none" stroke="${B}" stroke-width="2.2"/>` + P(poly(ring(50, 44, 5, 4.6, 2)), B),
  // 22 Craftsman — a hammer
  (F) => '<g transform="rotate(-38 50 50)">' + R(47, 41, 6, 32, F, 2) + P('M35 29.5h22.5c3.2 0 5.5 2.4 5.5 5.5v4.6c0 1.5-1.1 2.6-2.6 2.6H35c-2 0-3.2-1.2-3.2-3.1V33c0-2 1.3-3.5 3.2-3.5z', F) + '</g>',
  // 23 Merchant — scales
  (F) => R(48.3, 32, 3.4, 34, F) + C(50, 31, 3.2, F) + R(29, 35.8, 42, 3.4, F, 1.7) + S('M33 39l-5.5 13M33 39l5.5 13M67 39l-5.5 13M67 39l5.5 13', F, 1.7)
    + P('M25.5 52h15a7.5 7.5 0 0 1-15 0z', F) + P('M59.5 52h15a7.5 7.5 0 0 1-15 0z', F) + P('M39 71h22l-3.2-5H42.2z', F),
  // 24 Trader — a sailing ship
  (F) => P('M26 59h48l-7 10.5H33z', F) + R(48.6, 27, 2.8, 32, F) + P('M52.6 30c8.4 5.2 12.6 13.6 12.6 25H52.6z', F) + P('M47.4 33.5c-6.3 5.2-10 12.6-10.6 21.5h10.6z', F) + P('M51.4 27l6.4 2.4-6.4 2.4z', F),
  // 25 Notable — a laurel wreath
  (F) => laurel(F) + P(poly(ring(50, 38, 5, 6.5, 2.7)), F),
  // 26 Mentor — an open book
  (F, B) => P('M50 38.5c-6.2-4.2-13.6-5.2-22-4.2v29.4c8.4-1 15.8 0 22 4.2z', F) + P('M50 38.5c6.2-4.2 13.6-5.2 22-4.2v29.4c-8.4-1-15.8 0-22 4.2z', F)
    + S('M50 39v28', B, 1.8) + S('M33 42.5c4.2-.4 8.4.3 12 2.1M33 48.5c4.2-.4 8.4.3 12 2.1M33 54.5c4.2-.4 8.4.3 12 2.1M67 42.5c-4.2-.4-8.4.3-12 2.1M67 48.5c-4.2-.4-8.4.3-12 2.1M67 54.5c-4.2-.4-8.4.3-12 2.1', B, 1.6),
  // 27 Veteran — three chevrons
  (F) => [29, 41.5, 54].map((y) => P(`M32 ${y + 8.5}L50 ${y}l18 ${8.5}v7.2L50 ${y + 7.2}l-18 ${8.5}z`, F)).join(''),
  // 28 Leader — a banner
  (F) => R(33.5, 28, 3.6, 44, F, 1.6) + C(35.3, 27.5, 3, F) + P('M37.1 31.5c6.2-3.1 11.8 3.1 18 0s10.3-2 13.4 0v20.5c-3.1-2-7.2-3.1-13.4 0s-11.8-3.1-18 0z', F),
  // 29 Captain — an anchor
  (F) => `<circle cx="50" cy="31" r="4.4" fill="none" stroke="${F}" stroke-width="3.2"/>` + R(48.1, 35, 3.8, 33.5, F) + R(39.5, 39.5, 21, 3.8, F, 1.9)
    + S('M29.5 54.5c1 9.2 9.7 15.3 20.5 15.3S69.5 63.7 70.5 54.5', F, 4.2) + P('M25.8 57.5l3.2-8 5.6 6z', F) + P('M74.2 57.5l-3.2-8-5.6 6z', F),
  // 30 Commander — crossed swords
  (F) => sword(F, -42) + sword(F, 42),
  // 31 Elder — an old oak
  (F) => C(50, 40, 12.5, F) + C(38.5, 46.5, 9.5, F) + C(61.5, 46.5, 9.5, F) + C(43.5, 33, 8.5, F) + C(56.5, 33, 8.5, F)
    + P('M46.6 70V55.5h6.8V70z', F) + P('M38 70.5c4-2.4 8-3 12-3s8 .6 12 3z', F),
  // 32 Patron — a column
  (F, B) => P('M32.5 32h35v3.6h-35z', F) + C(33, 35.8, 2.8, F) + C(67, 35.8, 2.8, F) + P('M37 35.6h26l-3.2 4.4H40.2z', F) + R(40.5, 40, 19, 24.5, F)
    + S('M45 42.5v19.5M50 42.5v19.5M55 42.5v19.5', B, 1.9) + P('M37 64.5h26v3H37z', F) + P('M34 67.5h32V71H34z', F),
  // 33 Noble — a fleur-de-lis
  (F, B) => P('M50 25c-5.2 6-7.8 12.2-7.8 18.4 0 5.2 2.6 9.3 7.8 12.4 5.2-3.1 7.8-7.2 7.8-12.4 0-6.2-2.6-12.4-7.8-18.4z', F)
    + P('M46.5 57c-8.4 0-14.5-5.2-14.5-12.4 0-4.6 3.1-7.2 6.7-7.2 3.1 0 5.2 2.1 5.2 4.7 0 2.1-1.5 3.5-3.4 3.5 1.1 3.7 3.7 6.8 7 7.9z', F)
    + P('M53.5 57c8.4 0 14.5-5.2 14.5-12.4 0-4.6-3.1-7.2-6.7-7.2-3.1 0-5.2 2.1-5.2 4.7 0 2.1 1.5 3.5 3.4 3.5-1.1 3.7-3.7 6.8-7 7.9z', F)
    + R(36.5, 56, 27, 5, F, 1.6) + S('M37 56.2h26', B, 1.2) + P('M45 61l-4.4 9.5h4.8l4.6-6.2 4.6 6.2h4.8L55 61z', F),
  // 34 Baron — a castle keep
  (F, B) => P('M35.5 70V40.5h-2V28.5h6.4v4.8h5V28.5h10.2v4.8h5V28.5h6.4v12h-2V70z', F) + P('M44.6 70v-8.4a5.4 5.4 0 0 1 10.8 0V70z', B) + R(47.9, 43.5, 4.2, 8, B, 2.1),
  // 35 Count — a sealed charter
  (F, B) => R(37, 32.5, 26, 34, F) + R(32.5, 28.5, 35, 7, F, 3.5) + R(32.5, 63.5, 35, 7, F, 3.5) + S('M33 35.6h34M33 63.4h34', B, 1.3)
    + S('M41.5 41.5h17M41.5 47h17M41.5 52.5h10', B, 1.9) + `<circle cx="60" cy="58.5" r="7.6" fill="${F}" stroke="${B}" stroke-width="2.2"/>` + C(60, 58.5, 2.6, B),
  // 36 Duke — a crown
  (F, B) => P('M29 62.5l-3-24.5 12.8 11.2L50 31l11.2 18.2L74 38l-3 24.5z', F) + R(29, 64, 42, 6.4, F, 1.6) + C(26, 36.2, 3.2, F) + C(50, 28.6, 3.4, F) + C(74, 36.2, 3.2, F)
    + C(39.5, 67.2, 1.7, B) + C(50, 67.2, 1.9, B) + C(60.5, 67.2, 1.7, B),
  // 37 Prince — an orb and cross
  (F, B) => C(50, 55, 15.5, F) + S('M34.8 53.5h30.4M50 39.5v15.4', B, 2.6) + R(47.9, 24.5, 4.2, 15.5, F) + R(42.8, 29, 14.4, 4.2, F),
  // 38 Champion — a trophy
  (F, B) => P('M37 29.5h26v9.5c0 9.3-5.6 15.4-13 15.4S37 48.3 37 39z', F) + S('M37.5 33.5h-5.2c0 7.4 3.2 11.6 8.4 12.6M62.5 33.5h5.2c0 7.4-3.2 11.6-8.4 12.6', F, 3.4)
    + R(47.4, 53, 5.2, 8.5, F) + P('M38.5 70.5v-4.2c0-2.2 1.6-4.3 4.2-4.3h14.6c2.6 0 4.2 2.1 4.2 4.3v4.2z', F) + P(poly(ring(50, 39.5, 5, 6, 2.5)), B),
  // 39 Hero — a burning torch
  (F, B) => P('M45.5 52.5h9l-2.6 19.5h-3.8z', F) + S('M46 58.5h8M46.6 64h6.8', B, 1.6) + P('M38.5 46.5h23l-4 7h-15z', F)
    + P('M50 18.5c4.4 6.6 12.6 10.6 12.6 18.6 0 5.6-5 9-12.6 9s-12.6-3.4-12.6-9c0-5.2 2.8-8.4 5.6-10.8.6 3.8 2.2 5.8 4.4 6.4-1-5.4-.2-9.8 2.6-14.2z', F)
    + P('M50 31.5c2.6 3.4 5.2 5.6 5.2 8.8 0 2.6-2.2 4.2-5.2 4.2s-5.2-1.6-5.2-4.2c0-3.2 2.6-5.4 5.2-8.8z', B),
  // 40 Star — a star
  (F) => P(poly(ring(50, 51.5, 5, 22, 9.4)), F),
  // 41 Celebrity — a microphone
  (F, B) => R(41.5, 25.5, 17, 26, F, 8.5) + S('M42.5 33.5h15M42.5 38.5h15M42.5 43.5h15', B, 1.7) + S('M36.5 44c0 8.2 6 13.8 13.5 13.8S63.5 52.2 63.5 44', F, 3.4)
    + R(48.3, 57.5, 3.4, 9, F) + R(39.5, 66, 21, 4.4, F, 2.2),
  // 42 Tycoon — a cut diamond
  (F, B) => P('M35.5 31.5h29l10.5 12.5L50 72 25 44z', F) + S('M25.5 44h49M42.5 31.8L38.5 44 50 71.5 61.5 44l-4-12.2M38.5 44L50 31.8 61.5 44', B, 1.7),
  // 43 Magnate — gold bars
  (F, B) => [[26, 56.5], [51, 56.5], [38.5, 42.5]].map(([x, y]) => P(`M${x + 3.5} ${y}h16l3.5 12.5H${x}z`, F) + S(`M${x + 5.5} ${y + 3}h12`, B, 1.5)).join(''),
  // 44 Mogul — a skyline
  (F, B) => P('M27 71V47h11v24zM40 71V33l7-5 7 5v38zM56 71V41h17v30z', F) + S('M47 28v-5', F, 2)
    + S('M44 38.5h6M44 44.5h6M44 50.5h6M44 56.5h6M44 62.5h6M60 46.5h3M66 46.5h3M60 52.5h3M66 52.5h3M60 58.5h3M66 58.5h3M30.5 52.5h4M30.5 58.5h4', B, 2.2),
  // 45 Icon — a figure with a halo
  (F, B) => `<ellipse cx="50" cy="28.5" rx="11.5" ry="3.8" fill="none" stroke="${F}" stroke-width="3"/>` + P('M30.5 71.5c0-11.4 8.7-18.6 19.5-18.6s19.5 7.2 19.5 18.6z', F)
    + `<circle cx="50" cy="43" r="9.4" fill="${F}" stroke="${B}" stroke-width="2.4"/>`,
  // 46 Luminary — a crescent moon and a star
  (F) => P('M53 27.5a22.5 22.5 0 1 0 19.5 33.8A18.5 18.5 0 0 1 53 27.5z', F) + P(poly(ring(60.5, 41.5, 4, 8.5, 2.6)), F),
  // 47 Myth — a great wing
  (F, B) => P('M27 64c11-1.5 19.5-7.6 24-18 3.4-7.6 10.2-13.4 20.5-16.2-1 8.4-4.4 13.6-9.8 16.6 4.4.2 7.6-.8 9.8-2-2 6.4-6.4 9.6-12 10.8 3.2.9 6.4.9 8.8-.2-3.4 5.4-9 8.4-15.4 8.6 2 1 4.3 1.4 6.4.9-5.6 4.4-15.6 4.7-25.4 1.6z', F)
    + S('M40 61c6-2.5 11-6.6 14.5-12.5M47.5 62.2c5.6-1.6 10-4.6 13.3-9M53.5 63.5c4.2-.9 7.6-2.8 10.4-5.6', B, 1.5),
  // 48 Titan — a mountain
  (F, B) => P('M23 71l17.5-28.5 7 10 9.5-19L77 71z', F) + P('M57 33.8l-5.6 11.2 3.6-1.9 2.6 2.7 2.7-2.5 3.4 1.5z', B),
  // 49 Immortal — infinity
  (F) => S('M50 50c-4.2-5.2-8.4-9.4-13.6-9.4a9.4 9.4 0 0 0 0 18.8c5.2 0 9.4-4.2 13.6-9.4s8.4-9.4 13.6-9.4a9.4 9.4 0 0 1 0 18.8c-5.2 0-9.4-4.2-13.6-9.4z', F, 5.8),
  // 50 Legendary — the sun
  (F) => C(50, 50, 10.5, F) + around(50, 50, 12, 0, -90).map((_, i) => {
    const a = ((-90 + i * 30) * Math.PI) / 180, b = 0.2;
    const p = (r: number, d: number): [number, number] => [50 + r * Math.cos(a + d), 50 + r * Math.sin(a + d)];
    return P(poly([p(13.5, -b), p(23, 0), p(13.5, b)]), F);
  }).join(''),
];

// ── Frames ───────────────────────────────────────────────────────────────────

const FALLEN_R = [46, 43.5, 46.5, 41, 45.5, 44, 39.5, 46, 43, 45.5, 42.5, 46.5, 44.5, 40.5, 45.5, 43.5];
const SHIELD = 'M50 3.5L90.5 13.5V45c0 26.5-17.5 43-40.5 52C27 88 9.5 71.5 9.5 45V13.5z';

/** The outer shape of each band (path), with the centre of its field. */
function frameShape(b: number): { d: string; cy: number } {
  switch (b) {
    case 0: return { d: poly(FALLEN_R.map((r, i) => { const a = ((i * 22.5 - 90) * Math.PI) / 180; return [50 + r * Math.cos(a), 50 + r * Math.sin(a)] as [number, number]; })), cy: 50 };
    case 1: return { d: 'M50 4a46 46 0 1 1 0 92a46 46 0 1 1 0-92z', cy: 50 };
    case 2: return { d: 'M26 6h48a20 20 0 0 1 20 20v48a20 20 0 0 1-20 20H26A20 20 0 0 1 6 74V26A20 20 0 0 1 26 6z', cy: 50 };
    case 3: return { d: poly(around(50, 50, 8, 48, -67.5)), cy: 50 };
    case 4: return { d: 'M50 5a45 45 0 1 1 0 90a45 45 0 1 1 0-90z', cy: 50 };
    case 5: return { d: SHIELD, cy: 47 };
    case 6: return { d: lobes(16, 39.5, 8.2), cy: 50 };
    case 7: return { d: poly(ring(50, 50, 8, 49, 40.5, -90)), cy: 50 };
    case 8: return { d: poly(around(50, 50, 6, 49, -90)), cy: 50 };
    default: return { d: 'M50 9a41 41 0 1 1 0 82a41 41 0 1 1 0-82z', cy: 50 };
  }
}

/** A scalloped rosette: the outline of n round lobes (radius lr) on a circle of radius r. */
function lobes(n: number, r: number, lr: number): string {
  const half = Math.PI / n;
  const h = Math.sqrt(Math.max(0, lr * lr - (r * Math.sin(half)) ** 2));
  const rv = r * Math.cos(half) + h;
  const valleys = around(50, 50, n, rv, -90 + 180 / n);
  const large = rv > r ? 1 : 0;
  let d = `M${f1(valleys[n - 1][0])} ${f1(valleys[n - 1][1])}`;
  for (const [x, y] of valleys) d += `A${lr} ${lr} 0 ${large} 1 ${f1(x)} ${f1(y)}`;
  return d + 'Z';
}

interface Opt { size?: number; animate?: boolean }

/** The emblem of status n as SVG text. */
export function statusEmblemSvg(n: number, opt: Opt = {}): string {
  const N = clampN(n);
  const b = bandIndex(N);
  const p = PAL[b];
  const size = opt.size ?? 96;
  const small = size <= 32;
  const anim = opt.animate !== false && b === 9;
  const { d, cy } = frameShape(b);
  const fr = small ? 33.5 : 30.5;
  const gs = small ? 1.24 : 1;
  const out: string[] = [];
  const name = STATUS_NAMES[N - 1];

  // Gradients
  const stops = p.sheen
    ? `<stop offset="0" stop-color="${p.m[0]}"/><stop offset=".38" stop-color="${p.m[1]}"/><stop offset=".55" stop-color="${p.m[0]}"/><stop offset=".72" stop-color="${p.m[1]}"/><stop offset="1" stop-color="${p.m[2]}"/>`
    : `<stop offset="0" stop-color="${p.m[0]}"/><stop offset=".55" stop-color="${p.m[1]}"/><stop offset="1" stop-color="${p.m[2]}"/>`;
  out.push('<defs>',
    `<linearGradient id="m" x1=".12" y1="0" x2=".88" y2="1">${stops}</linearGradient>`,
    `<radialGradient id="f" cx=".42" cy=".36" r=".75"><stop offset="0" stop-color="${p.field[0]}"/><stop offset="1" stop-color="${p.field[1]}"/></radialGradient>`,
    `<clipPath id="c"><path d="${d}"/></clipPath>`);
  if (!small) out.push('<filter id="s" x="-20%" y="-20%" width="140%" height="140%"><feDropShadow dx="0" dy="1.2" stdDeviation="1.1" flood-color="#000" flood-opacity=".55"/></filter>');
  if (b === 9) {
    out.push(`<radialGradient id="g" cx=".5" cy=".5" r=".5"><stop offset=".55" stop-color="#ffe9a8" stop-opacity=".9"/><stop offset=".8" stop-color="#d07cff" stop-opacity=".35"/><stop offset="1" stop-color="#8a2be2" stop-opacity="0"/></radialGradient>`,
      '<filter id="l" x="-30%" y="-30%" width="160%" height="160%"><feGaussianBlur stdDeviation="2.2" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>');
  }
  out.push('</defs>');
  if (anim) {
    out.push('<style>.r{transform-origin:50px 50px;animation:r 36s linear infinite}.h{transform-origin:50px 50px;animation:h 3.2s ease-in-out infinite}'
      + '@keyframes r{to{transform:rotate(360deg)}}@keyframes h{0%,100%{opacity:.55;transform:scale(.97)}50%{opacity:1;transform:scale(1.03)}}'
      + '@media (prefers-reduced-motion:reduce){.r,.h{animation:none}}</style>');
  }

  // Mythic: the living light behind the frame.
  if (b === 9) {
    out.push(`<circle class="h" cx="50" cy="50" r="50" fill="url(#g)"/>`);
    const rays = small ? 12 : 24;
    out.push(`<g class="r">${around(50, 50, rays, 0).map((_, i) => {
      const a = ((-90 + (i * 360) / rays) * Math.PI) / 180, w = small ? 0.13 : 0.07, long = i % 2 ? 44 : 49.5;
      const q = (r: number, dd: number): [number, number] => [50 + r * Math.cos(a + dd), 50 + r * Math.sin(a + dd)];
      return `<path d="${poly([q(36, -w), q(long, 0), q(36, w)])}" fill="${i % 2 ? '#ffd27a' : '#fff4c8'}"/>`;
    }).join('')}</g>`);
  }

  // The frame
  const edgeW = small ? 3.4 : 2.2;
  out.push(`<path d="${d}" fill="url(#m)" stroke="${p.edge}" stroke-width="${edgeW}" stroke-linejoin="round"/>`);

  if (!small) {
    const r = rnd(N * 97 + 13);
    switch (b) {
      case 0: // cracks and a dull red glow
        out.push(`<g clip-path="url(#c)" fill="none" stroke="#77777f" stroke-width="1.1" stroke-linecap="round" opacity=".85">`
          + '<path d="M18 22l7 6 2 7M80 16l-5 8 1 6-4 4M90 60l-8 2-3 6M24 84l6-7 6 0M8 46l9 1M60 92l-2-8"/></g>');
        break;
      case 1: // rivets
        for (const [x, y] of around(50, 50, 10, 40.5, -90)) out.push(`<circle cx="${f1(x)}" cy="${f1(y)}" r="2.5" fill="#9aa1aa" stroke="#1a1c20" stroke-width=".8"/><circle cx="${f1(x - .7)}" cy="${f1(y - .7)}" r=".8" fill="#e3e7ec"/>`);
        break;
      case 2: // wood grain
        out.push(`<g clip-path="url(#c)" fill="none" stroke="#4d2c10" stroke-width=".9" opacity=".45">`
          + [10, 14, 18].map((k) => `<rect x="${6 + k / 2}" y="${6 + k / 2}" width="${88 - k}" height="${88 - k}" rx="${20 - k / 3}"/>`).join('')
          + '<path d="M12 30c8 3 10 9 6 16M88 62c-7-2-10-8-7-15M30 90c3-6 9-8 15-6"/><ellipse cx="80" cy="22" rx="3" ry="2"/></g>');
        break;
      case 3: // granite speckles
        out.push(`<g clip-path="url(#c)">`);
        for (let i = 0; i < 70; i++) {
          const a = r() * Math.PI * 2, rr = 34 + r() * 14;
          out.push(`<circle cx="${f1(50 + rr * Math.cos(a))}" cy="${f1(50 + rr * Math.sin(a))}" r="${f1(.5 + r() * .9)}" fill="${r() > .5 ? '#3c4046' : '#f4f5f6'}" opacity="${f1(.4 + r() * .4)}"/>`);
        }
        out.push('</g>');
        break;
      case 4: // beaded rim
        for (const [x, y] of around(50, 50, 28, 40.6, -90)) out.push(`<circle cx="${f1(x)}" cy="${f1(y)}" r="1.7" fill="#ffd7a8" stroke="#6b3910" stroke-width=".6"/>`);
        break;
      case 5: // a shine across the shield
        out.push(`<g clip-path="url(#c)"><path d="M8 40L48 0h12L8 52z" fill="#fff" opacity=".45"/><path d="M8 58L66 0h5L8 63z" fill="#fff" opacity=".3"/></g>`,
          `<path d="M50 9L85 17.6V45c0 23-15 37.4-35 45.4C30 82.4 15 68 15 45V17.6z" fill="none" stroke="${p.edge}" stroke-width=".9" opacity=".6"/>`);
        break;
      case 6: // rays between the lobes, a dotted inner rim
        for (const [x, y] of around(50, 50, 32, 37, -90)) out.push(`<circle cx="${f1(x)}" cy="${f1(y)}" r=".9" fill="#7a5205"/>`);
        break;
      case 7: // bevel and small gems
        out.push(`<path d="${poly(ring(50, 50, 8, 43.5, 36, -90))}" fill="none" stroke="#ffffff" stroke-width="1" opacity=".7"/>`);
        for (const [x, y] of around(50, 50, 8, 44.5, -90)) out.push(`<circle cx="${f1(x)}" cy="${f1(y)}" r="1.7" fill="#eaf4ff" stroke="#6a7d95" stroke-width=".6"/>`);
        break;
      case 8: { // facets
        const v = around(50, 50, 6, 49, -90), w2 = around(50, 50, 6, 37, -90);
        const tones = ['#ffffff', '#bdf0ff', '#6cc9f2', '#2f8fc6', '#5bbbe8', '#d8f7ff'];
        v.forEach((pt, i) => {
          const nx = v[(i + 1) % 6], ix = w2[(i + 1) % 6], iy = w2[i];
          out.push(`<path d="${poly([pt, nx, ix, iy])}" fill="${tones[i]}" opacity=".55"/>`);
          out.push(`<path d="M${f1(pt[0])} ${f1(pt[1])}L${f1(iy[0])} ${f1(iy[1])}" stroke="#ffffff" stroke-width=".8" opacity=".8"/>`);
        });
        out.push(`<path d="${poly(w2)}" fill="none" stroke="#e9fbff" stroke-width="1" opacity=".9"/>`);
        break;
      }
      default: // sparkles of light on the ring
        for (const [x, y] of around(50, 50, 8, 36.5, -67.5)) out.push(`<path d="${poly(ring(x, y, 4, 2.6, .7))}" fill="#fffbe6"/>`);
    }
  }

  // The field and its ring
  out.push(`<circle cx="50" cy="${cy}" r="${fr}" fill="url(#f)" stroke="${p.ring}" stroke-width="${small ? 2.4 : 1.8}"/>`);
  if (!small) out.push(`<circle cx="50" cy="${cy}" r="${fr - 2.6}" fill="none" stroke="#000" stroke-opacity=".28" stroke-width="1"/>`);

  // The figure
  const glyph = GLYPHS[N - 1](p.fig, p.field[1]);
  const tf = `translate(50 ${cy}) scale(${gs}) translate(-50 -50)`;
  const flt = b === 9 ? ' filter="url(#l)"' : small ? '' : ' filter="url(#s)"';
  out.push(`<g transform="${tf}"${flt}>${glyph}</g>`);

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="${size}" height="${size}" role="img" aria-label="${name}"><title>${name}</title>${out.join('')}</svg>`;
}

const URI_CACHE = new Map<string, string>();
/** The emblem as a data: URI, for an <img> (each image keeps its own ids; memoised). */
export function statusEmblemUri(n: number, opt: Opt = {}): string {
  const small = (opt.size ?? 96) <= 32;
  const key = `${clampN(n)}:${small ? 's' : 'l'}:${opt.animate === false ? 0 : 1}`;
  let u = URI_CACHE.get(key);
  if (!u) {
    u = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(statusEmblemSvg(n, { size: small ? 32 : 96, animate: opt.animate }));
    URI_CACHE.set(key, u);
  }
  return u;
}
